import { Fragment, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import apiCall, { makeUrl } from "../../../utils/apiCall";
import { Formik } from "formik";
import { useParams } from "react-router-dom";
import { Prompt } from "@helloaudio/router";
import { Row, Col, Form, ProgressBar } from "react-bootstrap";

import Billing from "../Billing/BillingInfo.js";
import GetEpisode from "./GetEpisode";
import EditEpisode from "./EditEpisode";

import moment from "moment";
import toast from "react-hot-toast";

import "react-datetime/css/react-datetime.css";
import { StateInterface } from "../../../InterfaceType/Interface";

const Episode = (props: any) => {
  const params = useParams();
  return <EpisodeOld {...{ ...props, params }} />;
};

function EpisodeOld(props: any) {
  const formValue = useSelector((state: StateInterface) => state.basic);
  const dispatch = useDispatch();
  const [, setAudioFileError] = useState(false);
  const [, setBuyCreditsModal] = useState(false);

  useEffect(() => {
    window.addEventListener("beforeunload", handleUnload);
    const existingFeedEpisodeId = props.params.id;
    if (existingFeedEpisodeId) {
      dispatch({ type: "GET_FEED_EPISODE", feedEpisodeId: existingFeedEpisodeId });
    }
    dispatch({ type: "GET_TAG_SUGGESTIONS" });

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  useEffect(() => {
    setAudioFileError(false);
  }, [props]);

  const handleUnload = (e: any) => {
    const message =
      "Warning! Navigating away while uploading is in progress will stop the upload. Do you wish to continue?";
    if (formValue.singleEpisode) {
      e.preventDefault();
      (e || window.event).returnValue = message; // Gecko + IE
      return message;
    }
  };

  const downloadTranscript = () => {
    const blob = new Blob([formValue.feedEpisode.transcript], {
      type: "octet/stream",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", formValue.feedEpisode.title + ".txt");
    link.click();
  };

  const getEmbedCode = (episodeId: string, code: string) => {
    navigator.clipboard
      .writeText(
        `<iframe src="${process.env.API_URL}/player?episodeId=${episodeId}&code=${code}" width="400" height="100" scrolling="no" frameBorder="0" style="width: 400px; height: 100px; border: 0; overflow: hidden;"></iframe>`,
      )
      .then(function () {
        toast.success("Copied to clipboard!", { duration: 3000 });
      });
  };

  const getEmbedCodeSlim = (episodeId: string, code: string) => {
    navigator.clipboard
      .writeText(
        `<iframe src="${process.env.API_URL}/player?episodeId=${episodeId}&code=${code}&simple=true" height="35" scrolling="no" frameBorder="0" style="width: 100%; max-width: 900px; height: 35px; border: 0; overflow: hidden;"></iframe>`,
      )
      .then(function () {
        toast.success("Copied to clipboard!", { duration: 3000 });
      });
  };

  const getEpisodeShareCode = (episodeId: string, code: string) => {
    navigator.clipboard
      .writeText(`${process.env.API_URL}/player?episodeId=${episodeId}&code=${code}&centered=true`)
      .then(function () {
        toast.success("Copied to clipboard!", { duration: 3000 });
      });
  };

  const existingFeedEpisodeId = props.params.id;
  const totalRemasters =
    formValue.localUser.monthly_remasters_remaining +
    formValue.localUser.alacarte_remasters_remaining;

  const getSignedUrl = async (file: { name: any; type: any }, callback: (arg0: any) => void) => {
    const getSignedUrlApiCall = await apiCall();
    const result = await getSignedUrlApiCall.post(makeUrl("/audio-file"), {
      episodeId: existingFeedEpisodeId,
      feedId: formValue.feedEpisode.feed_id,
      filename: file.name,
      contentType: file.type,
      cachebust: moment().unix() + Math.random(),
    });
    dispatch({ type: "START_SINGLE_EPISODE_UPLOAD" });
    callback(result.data);
  };

  const onSubmitWrapper = (values: any) => {
    dispatch({
      type: "UPDATE_FEED_EPISODE",
      // Other action attributes, keep these alpha-ordered
      description: values.description,
      dripAfter: values.dripAfter,
      episodeType: values.episodeType,
      expirationDripAfter: values.expiry === "after" ? values.expirationDripAfter : null,
      explicit: values.explicit,
      feedEpisodeId: existingFeedEpisodeId,
      feedId: values.feedId,
      listenerTagsExpression: {
        or: values.tags.map((tag: { name: string }) => {
          return {
            some: [{ var: "tags" }, { "==": [{ var: "" }, tag.name] }],
          };
        }),
      },
      ordering: values.ordering,
      publishDate: moment(values.publish_date).unix(),
      refreshEpisodes: values.refreshEpisodes,
      season: values.season,
      silent: values.silent ?? true,
      title: values.title,
      universalFooter: values.universalFooter,
      useDolby: values.useDolby,
      callback: () =>
        dispatch({
          type: "GET_FEED_EPISODE",
          feedEpisodeId: existingFeedEpisodeId,
          silent: true,
        }),
    });
  };

  const handleSubmitWrapper = (options: any) => (values: any) => {
    onSubmitWrapper({ ...values, ...options });
  };

  return (
    <Fragment>
      {formValue && formValue.localUser.feedLimit === 0 ? (
        <Billing />
      ) : (
        <Fragment>
          <div className="tw-rounded">
            <p className="audio-inbox-title-top">Edit Episode</p>
          </div>
          <Prompt
            when={!!formValue.singleEpisode}
            message="Warning! Navigating away while uploading is in progress can lead to bugs. Do you wish to continue?"
          />
          <Formik
            enableReinitialize
            initialValues={{
              title: existingFeedEpisodeId ? formValue.feedEpisode.title : "",
              description: existingFeedEpisodeId ? formValue.feedEpisode.description : "",
              ordering: existingFeedEpisodeId ? formValue.feedEpisode.ordering : "",
              publish_date: existingFeedEpisodeId ? formValue.feedEpisode.publish_date : "",
              dripAfter: existingFeedEpisodeId ? formValue.feedEpisode.drip_after : "",
              transcript: existingFeedEpisodeId ? formValue.feedEpisode.transcript : "",
              explicit: existingFeedEpisodeId ? formValue.feedEpisode.explicit : "",
              expiry: existingFeedEpisodeId
                ? formValue.feedEpisode.expiration_drip_after
                  ? "after"
                  : "never"
                : "never",
              expirationDripAfter: existingFeedEpisodeId
                ? formValue.feedEpisode.expiration_drip_after
                : "",
              tags: existingFeedEpisodeId ? formValue.feedEpisode.tags : [],
              universalFooter: existingFeedEpisodeId ? formValue.feed.universal_footer : null,
              feedId: existingFeedEpisodeId ? formValue.feedEpisode.feed_id : null,
              remasterStatus: existingFeedEpisodeId ? formValue.feedEpisode.dolby_job_id : "",
              useDolby: existingFeedEpisodeId ? formValue.feedEpisode.use_dolby : false,
              season: existingFeedEpisodeId ? formValue.feedEpisode.season : 1,
              episodeType: existingFeedEpisodeId ? formValue.feedEpisode.episode_type : "full",
            }}
            validate={(values) => {
              const errors: any = {};
              if (!values.title) {
                errors.title = "Give your episode a title!";
              }
              if (formValue.feed.use_seasons && values.season < 1) {
                errors.season = "Season must be 1 or greater.";
              }
              if (formValue.feedEpisode.feed_type === "drip" && values.dripAfter < 0) {
                errors.dripAfter = "Drip After Days must be 0 or greater.";
              }
              if (formValue.feedEpisode.feed_type === "drip" && values.dripAfter > 365) {
                errors.dripAfter = "Please choose a number from 0 to 365.";
              }
              if (
                formValue.feedEpisode.feed_type === "drip" &&
                formValue.feedEpisode.ordering === 1 &&
                values.dripAfter
              ) {
                errors.dripAfter =
                  "The feed requires at least one episode at the time of subscribing, therefore we can't delay the release of the first episode.";
              }
              if (
                formValue.feedEpisode.feed_type === "date" &&
                isNaN(moment(values.publish_date).unix())
              ) {
                errors.publishDate = "That is not a valid date";
              }
              if (values.expiry === "after" && values.expirationDripAfter < 1) {
                errors.expirationDripAfter = "Must be one or greater days";
              }
              return errors;
            }}
            onSubmit={onSubmitWrapper}
          >
            {({ values, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
              <Form noValidate onSubmit={(e) => handleSubmit(e)}>
                <Row>
                  <Col md={4} className="tw-mb-4 md:tw-mb-0">
                    <div className="m-auto tw-bg-white tw-p-4 tw-overflow-auto tw-flex tw-flex-col tw-rounded edit-episode-left-panel">
                      <div className="episode-background">
                        <GetEpisode
                          downloadTranscript={downloadTranscript}
                          errors={errors}
                          existingFeedEpisodeId={existingFeedEpisodeId}
                          feed={formValue?.feed}
                          feedEpisode={formValue?.feedEpisode}
                          feedId={formValue?.feedEpisode.feed_id}
                          feedTitle={formValue?.feedEpisode.feed_title}
                          getSignedUrl={getSignedUrl}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          onSubmit={handleSubmitWrapper({
                            refreshEpisodes: formValue?.feedEpisode.feed_id,
                            silent: false,
                          })}
                          setAudioFileError={setAudioFileError}
                          setBuyCreditsModal={setBuyCreditsModal}
                          setFieldValue={setFieldValue}
                          src={formValue?.feedEpisode.image}
                          totalRemasters={totalRemasters}
                          values={values}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col md={8} className="right-pan">
                    <div className="tw-bg-white tw-flex tw-flex-col tw-p-4 tw-pt-0 tw-overflow-auto tw-rounded">
                      <EditEpisode
                        existingFeedEpisodeId={existingFeedEpisodeId}
                        feedEpisode={formValue.feedEpisode}
                        feedId={formValue.feedEpisode.feed_id}
                        getEmbedCode={getEmbedCode}
                        getEmbedCodeSlim={getEmbedCodeSlim}
                        getEpisodeShareCode={getEpisodeShareCode}
                        handleSubmit={handleSubmit}
                        setFieldValue={setFieldValue}
                        values={values}
                      />
                    </div>
                  </Col>
                </Row>

                {formValue.singleEpisode && (
                  <ProgressBar
                    className="tw-mt-2"
                    animated
                    now={formValue.singleEpisode.progress}
                    label={`${formValue.singleEpisode.progress}%`}
                  />
                )}
              </Form>
            )}
          </Formik>
        </Fragment>
      )}
    </Fragment>
  );
}

export default Episode;
